import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import mainLogo from '../assets/images/main_logo.png';
import BIFApplicationForm from '../BIHForm/BIFApplicationForm';
import ContactUsForm from '../../pages/main/ContactUsForm';

const ProgramsPopover = ({ isOpen, onClose }) => {
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const programs = [
    'Design-A-Business',
    'Introduction to Costing & Management Accounting',
    'Business Acceleration',
    'Business Health Check',
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="absolute left-0 z-10 mt-2 w-72 rounded-xl border border-gray-100 bg-white p-4 shadow-lg sm:w-80 sm:p-6"
          ref={popoverRef}
        >
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base font-semibold text-gray-900 sm:text-lg">Our Programs</h3>
            <div className="space-y-2 sm:space-y-3">
              {programs.map((program, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 rounded-lg border border-gray-100 p-2.5 transition-colors hover:bg-gray-50 sm:space-x-3 sm:p-3"
                >
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-green-500 text-xs font-semibold text-white sm:h-8 sm:w-8 sm:text-sm">
                    {index + 1}
                  </div>
                  <span className="text-xs font-medium text-gray-700 sm:text-sm">{program}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BIFPopover = ({ isOpen, onClose }) => {
  const popoverRef = useRef(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const handleClickOutside = event => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleApplyClick = () => {
    setShowForm(true);
    onClose();
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 z-10 mt-2 w-80 rounded-xl border border-gray-100 bg-white p-4 shadow-lg sm:w-96 sm:p-6"
            ref={popoverRef}
          >
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                Botswana Innovation Fund (BIF)
              </h3>
              <div className="space-y-2 text-xs text-gray-600 sm:space-y-3 sm:text-sm">
                <p>Eligibility Requirements:</p>
                <ul className="list-inside list-disc space-y-0.5 sm:space-y-1">
                  <li>Completed "Design-A-Business " module</li>
                  <li>Completed "Introduction to Costing & Management Accounting" module</li>
                </ul>
                <p>Application Process:</p>
                <ol className="list-inside list-decimal space-y-0.5 sm:space-y-1">
                  <li>Company Details</li>
                  <li>Project Summary</li>
                  <li>Financials & Budget</li>
                  <li>Impact & Innovation Justification</li>
                  <li>Submit & Confirmation</li>
                </ol>
                <div className="mt-3 sm:mt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full rounded-md bg-gradient-to-r from-blue-500 to-green-500 px-3 py-2 text-xs font-semibold text-white hover:from-blue-600 hover:to-green-600 sm:px-4 sm:text-sm"
                    onClick={handleApplyClick}
                  >
                    Apply for BIF Funding
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <BIFApplicationForm isOpen={showForm} onClose={() => setShowForm(false)} />
    </>
  );
};

const TABS = [
  { id: 'company', label: 'Company Details' },
  // You can add more tabs here
];

const sampleCompany = {
  companyName: 'Acme Corporation',
  industry: '',
  address: '123 Tech Street',
  city: 'San Francisco',
  postalCode: '12345',
  country: 'United States',
  website: 'https://acme.com',
  phone: '+1 (555) 123-4567',
  email: '',
};

const MyHeader = ({ onApplyNow, onLogin }) => {
  const aboutRef = useRef(null);
  const programsRef = useRef(null);
  const [isBIFOpen, setIsBIFOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollTo = ref => ref.current?.scrollIntoView({ behavior: 'smooth' });

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <header className="flex flex-col gap-3 border-b border-b-gray-100 px-4 py-3 sm:gap-4 sm:px-6 md:flex-row md:items-center md:justify-between md:px-12 md:py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* <div className="flex h-8 w-8 items-center justify-center rounded-full ">
              <motion.img
                src={mainLogo}
                alt="BDIH Logo"
                className="mb-2 h-24 w-24 object-contain"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7, type: 'spring', stiffness: 120, damping: 10 }}
              />
            </div> */}
            <span className="text-lg font-bold sm:text-xl">411 • Group</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100 md:hidden"
              aria-label="Toggle mobile menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex md:gap-8">
          <div className="relative">
            <button
              onClick={() => setIsProgramsOpen(!isProgramsOpen)}
              className="font-medium hover:text-blue-600"
            >
              Programs
            </button>
            <ProgramsPopover isOpen={isProgramsOpen} onClose={() => setIsProgramsOpen(false)} />
          </div>
          <button onClick={() => scrollTo(aboutRef)} className="font-medium hover:text-blue-600">
            About
          </button>
          <button onClick={() => setContactOpen(true)} className="font-medium hover:text-blue-600">
            Contact
          </button>
        </nav>

        {/* Desktop Buttons */}
        <div className="hidden gap-3 md:flex md:gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="rounded-md bg-gradient-to-r from-blue-500 to-green-500 px-4 py-2 text-sm font-semibold text-white hover:from-blue-600 hover:to-green-600 md:px-5 md:text-base"
            onClick={onLogin}
            style={{ backgroundColor: 'transparent' }}
          >
            Login
          </motion.button>
          {/* <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="rounded-md bg-gradient-to-r from-blue-500 to-green-500 px-5 py-2 font-semibold text-white hover:from-blue-600 hover:to-green-600"
          onClick={onApplyNow}
        >
          Apply Now
        </motion.button> */}
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mobile-menu md:hidden"
            >
              <div className="space-y-3 border-t border-gray-200 pt-4">
                {/* Mobile Navigation */}
                <nav className="flex flex-col space-y-3">
                  <div className="relative">
                    <button
                      onClick={() => setIsProgramsOpen(!isProgramsOpen)}
                      className="flex w-full items-center justify-between py-2 text-left font-medium hover:text-blue-600"
                    >
                      Programs
                      <svg
                        className={`h-4 w-4 transition-transform ${isProgramsOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isProgramsOpen && (
                      <div className="ml-4 mt-2 space-y-2 border-l-2 border-gray-200 pl-4">
                        {[
                          'Design-A-Business',
                          'Introduction to Costing & Management Accounting',
                          'Business Acceleration',
                          'Business Health Check',
                        ].map((program, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-green-500 text-xs font-semibold text-white">
                              {index + 1}
                            </div>
                            <span className="text-sm text-gray-700">{program}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      scrollTo(aboutRef);
                      setIsMobileMenuOpen(false);
                    }}
                    className="py-2 text-left font-medium hover:text-blue-600"
                  >
                    About
                  </button>
                  <button
                    onClick={() => {
                      setContactOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="py-2 text-left font-medium hover:text-blue-600"
                  >
                    Contact
                  </button>
                </nav>

                {/* Mobile Buttons */}
                <div className="flex flex-col space-y-2 pt-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full rounded-md bg-gradient-to-r from-blue-500 to-green-500 px-4 py-2.5 text-sm font-semibold text-white hover:from-blue-600 hover:to-green-600"
                    onClick={() => {
                      onLogin();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Login
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contact Us Modal */}
        <AnimatePresence>
          {contactOpen && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="relative w-full max-w-xl"
              >
                <button
                  onClick={() => setContactOpen(false)}
                  className="absolute right-2 top-2 z-10 rounded-full bg-white/70 p-1.5 text-gray-700 shadow hover:bg-white sm:right-4 sm:top-4 sm:p-2"
                  aria-label="Close Contact Form"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5 sm:h-6 sm:w-6"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <ContactUsForm />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default MyHeader;
